import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    CheckCircle, ArrowSquareOut, FilmSlate, NumberCircleOne, Calendar,
    Ruler, Wrench, Pill, FirstAid, Cube, Lock, User,
} from "@phosphor-icons/react";
import { fmt, humanize, fmtDate, fmtDateTime, fmtAgo, EMPTY } from "./format";
import { isPostApproval } from "@/lib/caseStatus";

/* ─── Layout primitives ───────────────────────────────────────────── */
/* iter101.14 · Phase 3 — Restyled to match the doctor portal's
   `SectionCard` visual weight (softer border, tinted bg, teal label,
   no divider line). Same props (`title`, `action`, `Icon`, `testId`),
   drop-in replacement for the previous heavier bg-card + border-b
   header pattern used across the admin tab bodies. */
function Card({ title, action, children, testId, Icon }) {
    return (
        <section
            className="rounded-xl border border-border/70 bg-secondary/15 px-4 py-3.5 space-y-3"
            data-testid={testId}
        >
            {(title || action) && (
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-primary/85">
                        {Icon && <Icon size={12} weight="duotone" />}
                        {title}
                    </div>
                    {action}
                </div>
            )}
            <div>{children}</div>
        </section>
    );
}

function Field({ label, value, mono, testId }) {
    const isEmpty = value === undefined || value === null || (typeof value === "string" && !value.trim());
    return (
        <div data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
            <div className={`mt-1 text-sm ${mono ? "font-mono" : ""} ${isEmpty ? "text-muted-foreground" : ""}`}>
                {isEmpty ? EMPTY : value}
            </div>
        </div>
    );
}

function Metric({ label, value, sub }) {
    return (
        <div className="rounded-lg border border-border bg-secondary/30 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
            <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

/* ─── Aligner-sheet material resolver (admin reads from inventory) ── */
function useMaterialName(materialId) {
    const [name, setName] = useState(null);
    useEffect(() => {
        if (!materialId) return;
        // The /admin/inventory/materials list is small and cached after first
        // load; fetching the whole list and filtering is cheaper than a
        // per-id endpoint we'd otherwise have to add.
        api.get("/admin/inventory/materials")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                const hit = list.find((m) => m.id === materialId);
                setName(hit?.name || hit?.code || materialId);
            })
            .catch(() => setName(materialId));
    }, [materialId]);
    return name;
}

/* ─── Doctor approval banner ──────────────────────────────────────── */
function ApprovalBanner({ c }) {
    // iter101.5 — Use the shared POST_APPROVAL check so this stays in
    // lock-step with every other surface that renders approval state.
    // Previously the list was hardcoded here and missed
    // `approved_pending_mfg` / `awaiting_production_assignment`, which
    // meant this banner said "not yet approved" for cases that had
    // ALREADY been signed off by the doctor.
    const approvedAt =
        c?.timeline?.find?.((e) => e.action === "doctor_approved" || e.action === "approved")?.at
        || (isPostApproval(c?.status) ? c?.status_updated_at : null);
    if (!approvedAt) {
        return (
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-center gap-2" data-testid="treatment-not-approved">
                <FirstAid size={16} weight="fill" />
                <span>Plan is not yet doctor-approved — admin view will populate once the doctor signs off.</span>
            </div>
        );
    }
    return (
        <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 text-sm flex items-center gap-2" data-testid="treatment-approval-banner">
            <CheckCircle size={16} weight="fill" className="text-emerald-700" />
            <span>
                Approved by <span className="font-bold">{c.doctor_name || "the treating doctor"}</span> on{" "}
                <span className="font-bold">{fmtDateTime(approvedAt)}</span>
                {" "}({fmtAgo(approvedAt)})
            </span>
        </div>
    );
}

/* ─── MAIN TAB ────────────────────────────────────────────────────── */
export function TreatmentTab({ c }) {
    const plan = c?.plan || {};
    const spec = c?.manufacturing_spec || {};

    const alignerSheetName = useMaterialName(spec.aligner_sheet_material_id);
    const retainerSheetName = useMaterialName(spec.retainer_sheet_material_id);

    // Estimated completion date = approved_at + (max(upper, lower) * change_interval_days)
    const estCompletion = useMemo(() => {
        const maxStages = Math.max(plan.aligner_count_upper || 0, plan.aligner_count_lower || 0);
        const interval = plan.change_interval_days || 0;
        if (!maxStages || !interval) return null;
        const start = c?.timeline?.find?.((e) => e.action === "doctor_approved" || e.action === "approved")?.at
                   || c?.status_updated_at;
        if (!start) return null;
        const d = new Date(start);
        d.setDate(d.getDate() + maxStages * interval);
        return d.toISOString();
    }, [plan.aligner_count_upper, plan.aligner_count_lower, plan.change_interval_days, c?.timeline, c?.status_updated_at]);

    const productionStarted = ["in_production", "shipped", "in_treatment", "treatment_started", "completed"].includes(c?.status);

    return (
        <div className="space-y-5" data-testid="treatment-tab">
            <ApprovalBanner c={c} />

            {/* ── Plan Viewer + Walkthrough Video ─────────────────────── */}
            <Card title="Plan Viewer" Icon={ArrowSquareOut} testId="treatment-plan-viewer">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">3D plan viewer link</div>
                        {plan.external_link ? (
                            <a
                                href={plan.external_link.startsWith("http") ? plan.external_link : `https://${plan.external_link}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 transition"
                                data-testid="treatment-plan-viewer-link"
                            >
                                <ArrowSquareOut size={13} weight="bold" />
                                Open external viewer
                            </a>
                        ) : (
                            <div className="text-sm text-muted-foreground italic">No external plan viewer link provided.</div>
                        )}
                        {plan.external_link && (
                            <div className="mt-1.5 text-[10px] font-mono text-muted-foreground truncate">{plan.external_link}</div>
                        )}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
                            <FilmSlate size={11} className="inline mr-0.5" /> Walkthrough video
                        </div>
                        {plan.walkthrough_video_url ? (
                            <a
                                href={plan.walkthrough_video_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-sm font-semibold hover:bg-secondary transition"
                                data-testid="treatment-walkthrough-link"
                            >
                                <FilmSlate size={13} weight="fill" />
                                Watch walkthrough
                            </a>
                        ) : (
                            <div className="text-sm text-muted-foreground italic">No walkthrough video uploaded yet.</div>
                        )}
                    </div>
                </div>
            </Card>

            {/* ── Aligner Counts + Wear Schedule ──────────────────────── */}
            <Card title="Aligner counts & wear schedule" Icon={Calendar} testId="treatment-aligner-counts">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Metric label="Upper arch" value={plan.aligner_count_upper ?? "—"} sub="aligners" />
                    <Metric label="Lower arch" value={plan.aligner_count_lower ?? "—"} sub="aligners" />
                    <Metric label="Wear / aligner" value={plan.change_interval_days ?? "—"} sub="days" />
                    <Metric label="Total stages" value={Math.max(plan.aligner_count_upper || 0, plan.aligner_count_lower || 0) || "—"} sub="treatment depth" />
                    <Metric
                        label="Est. completion"
                        value={estCompletion ? fmtDate(estCompletion) : "—"}
                        sub={estCompletion ? `~${fmtAgo(estCompletion).replace(' ago','')} out` : "set by planner"}
                    />
                </div>
            </Card>

            {/* ── Bite Metrics ────────────────────────────────────────── */}
            <Card title="Bite metrics" Icon={Ruler} testId="treatment-bite-metrics">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Overjet</div>
                        <div className="mt-1 text-sm flex items-baseline gap-1.5">
                            <span className="font-mono">{plan.initial_overjet_mm ?? "—"} mm</span>
                            <span className="text-muted-foreground text-xs">→</span>
                            <span className="font-mono font-bold text-primary">{plan.final_overjet_mm ?? "—"} mm</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Overbite</div>
                        <div className="mt-1 text-sm flex items-baseline gap-1.5">
                            <span className="font-mono">{plan.initial_overbite_mm ?? "—"} mm</span>
                            <span className="text-muted-foreground text-xs">→</span>
                            <span className="font-mono font-bold text-primary">{plan.final_overbite_mm ?? "—"} mm</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total IPR</div>
                        <div className="mt-1 text-sm font-mono font-bold">{plan.total_ipr_mm ?? "—"} mm</div>
                    </div>
                </div>
            </Card>

            {/* ── Proposed Protocol ───────────────────────────────────── */}
            <Card title="Proposed protocol" Icon={Wrench} testId="treatment-protocol">
                <div className="space-y-4">
                    <Field label="Attachments" value={plan.proposed_attachments} testId="treatment-attachments" />
                    <Field label="IPR (by region)" value={plan.proposed_ipr} testId="treatment-ipr-region" />
                    <Field label="Elastics" value={plan.proposed_elastics} testId="treatment-elastics" />
                    <Field label="Planner notes" value={plan.planner_notes} testId="treatment-planner-notes" />
                    {plan.aux_buttons || plan.aux_bite_ramps || plan.aux_mandibular_advancement || plan.aux_precision_cuts || plan.aux_hooks ? (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">Auxiliaries</div>
                            <div className="flex flex-wrap gap-1.5">
                                {plan.aux_buttons && <Aux label="Buttons" />}
                                {plan.aux_bite_ramps && <Aux label="Bite ramps" />}
                                {plan.aux_mandibular_advancement && <Aux label="Mandibular advancement" />}
                                {plan.aux_precision_cuts && <Aux label="Precision cuts" />}
                                {plan.aux_hooks && <Aux label="Hooks" />}
                            </div>
                            {plan.aux_other_notes && (
                                <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{plan.aux_other_notes}</div>
                            )}
                        </div>
                    ) : null}
                </div>
            </Card>

            {/* ── Retention Plan ──────────────────────────────────────── */}
            <Card title="Retention plan" Icon={Pill} testId="treatment-retention">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field
                        label="Retainer type"
                        value={plan.retainer_type === "other" ? plan.retainer_other_text : humanize(plan.retainer_type)}
                        testId="treatment-retainer-type"
                    />
                    <Field
                        label="Recall schedule"
                        value={plan.retention_recall_months > 0 ? `Every ${plan.retention_recall_months} month${plan.retention_recall_months !== 1 ? "s" : ""}` : plan.retention_schedule}
                        testId="treatment-recall"
                    />
                    <Field label="Retention notes" value={plan.retention_schedule} testId="treatment-retention-notes" />
                </div>
            </Card>

            {/* ── Manufacturing Spec ──────────────────────────────────── */}
            <Card
                title="Manufacturing specification"
                Icon={Cube}
                testId="treatment-manufacturing"
                action={productionStarted && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
                        <Lock size={9} weight="bold" /> Locked
                    </span>
                )}
            >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field
                        label="Aligner sheet"
                        value={alignerSheetName || (spec.aligner_sheet_material_id ? "Loading…" : null)}
                        testId="treatment-aligner-sheet"
                    />
                    <Field
                        label="Retainer sheet"
                        value={retainerSheetName || (spec.retainer_sheet_material_id ? "Loading…" : null)}
                        testId="treatment-retainer-sheet"
                    />
                    <Field
                        label="Sheets per stage"
                        value={spec.sheets_per_stage}
                        testId="treatment-sheets-per-stage"
                    />
                    <Field
                        label="Aligner stages"
                        value={spec.aligner_stages_count}
                        testId="treatment-stages-count"
                    />
                    <Field
                        label="Retainer count"
                        value={spec.retainer_count}
                        testId="treatment-retainer-count"
                    />
                    <Field
                        label="Arches"
                        value={humanize(spec.arches)}
                        testId="treatment-arches"
                    />
                </div>
                {spec.notes && (
                    <div className="mt-4 pt-4 border-t border-border">
                        <Field label="Production notes" value={spec.notes} testId="treatment-production-notes" />
                    </div>
                )}
                {spec.set_by_name && spec.set_at && (
                    <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        <User size={11} /> Set by <span className="font-medium">{spec.set_by_name}</span> · {fmtDateTime(spec.set_at)}
                    </div>
                )}
            </Card>

            {/* ── Footer: read-only reminder ─────────────────────────── */}
            <div className="text-center text-xs text-muted-foreground py-2">
                <Lock size={10} className="inline mr-1" /> Admin view — all fields are read-only.
                Edits happen in the Planner portal.
            </div>
        </div>
    );
}

function Aux({ label }) {
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <CheckCircle size={10} weight="fill" /> {label}
        </span>
    );
}
